import { ReactNode, useEffect, useState } from "react";
import { LucideIcon } from "lucide-react";
import GradientHeader from "../util/GradientHeader";

export interface LegalSectionConfig {
  id: string;
  label: ReactNode;
  icon: LucideIcon;
}

interface LegalPageLayoutProps {
  title: ReactNode;
  description: ReactNode;
  icon: LucideIcon;
  sections: LegalSectionConfig[];
  tableOfContentsLabel: ReactNode;
  children: ReactNode;
}

export default function LegalPageLayout({
  title,
  description,
  icon: Icon,
  sections,
  tableOfContentsLabel,
  children,
}: LegalPageLayoutProps) {
  const [activeSection, setActiveSection] = useState(sections[0]?.id || "");

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const top = element.offsetTop;
          const height = element.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]);

  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: "smooth",
      });
      setActiveSection(id);
    }
  };

  return (
    <div className="page-enter mx-auto w-full max-w-6xl px-4 py-8 md:py-12">
      <div className="mb-10 text-center">
        <div className="bg-soluud-primary-light text-soluud-primary mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full">
          <Icon className="h-6 w-6" />
        </div>
        <GradientHeader className="mb-2">{title}</GradientHeader>
        <p className="text-soluud-text-secondary mx-auto max-w-2xl text-lg">
          {description}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
        {/* Sticky Navigation Sidebar */}
        <div className="md:col-span-1">
          <div className="border-soluud-border bg-soluud-background-card sticky top-24 rounded-xl border p-4 shadow-[--shadow-card]">
            <h3 className="text-soluud-text-tertiary mb-4 text-xs font-semibold tracking-wider uppercase">
              {tableOfContentsLabel}
            </h3>
            <nav className="space-y-1">
              {sections.map((section) => {
                const SectionIcon = section.icon;
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => scrollTo(section.id)}
                    className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? "bg-soluud-primary-subtle text-soluud-primary-hover border-soluud-primary border-l-2"
                        : "text-soluud-text-secondary hover:bg-soluud-background hover:text-soluud-text-primary"
                    }`}
                  >
                    <SectionIcon className="h-4.5 w-4.5 shrink-0" />
                    <span className="line-clamp-1">{section.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Content Area */}
        <div className="space-y-8 md:col-span-3">{children}</div>
      </div>
    </div>
  );
}
